import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { verifyProjectFunding } from "@/features/projects/services/projects-api.service";
import { isEscrowTopUp } from "@/features/projects/utils/escrow-balance";

export function PaymentReturnPage(): React.ReactElement {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { projectId: projectIdParam } = useParams<{ projectId?: string }>();
	const [searchParams] = useSearchParams();
	const [message, setMessage] = useState<string>(
		t("project.payments.verifyingFunding"),
	);
	// One verification request per order, not one per effect run. `main.tsx`
	// wraps the app in StrictMode, whose development double-invoke would
	// otherwise POST twice for the same payment -- the `cancelled` flag below
	// discards the superseded result but cannot un-send the request.
	//
	// The second run reuses the first run's promise rather than skipping:
	// skipping would strand the result, because the run that issued it has
	// already been cancelled by its own cleanup.
	const inFlightRef = useRef<{
		key: string;
		promise: ReturnType<typeof verifyProjectFunding>;
	} | null>(null);

	useEffect(() => {
		let cancelled = false;

		// The backend appends the project id to the configured return URL's
		// path, so a provider redirect arrives on the `:projectId` route. The
		// two query names are fallbacks for a return URL configured by hand to
		// carry the id in its query string instead.
		const projectId =
			projectIdParam ||
			searchParams.get("projectId") ||
			searchParams.get("project_id");
		const providerOrderId =
			searchParams.get("order_id") || searchParams.get("cf_order_id");

		async function run() {
			// Cashfree's bank-redirect carries only the order id; the backend
			// resolves the project from the funding session and returns it.
			if (!providerOrderId) {
				setMessage(t("project.payments.verificationFailed"));
				toast.error(t("project.payments.verificationFailed"));
				return;
			}

			const orderKey = `${projectId}:${providerOrderId}`;
			if (inFlightRef.current?.key !== orderKey) {
				inFlightRef.current = {
					key: orderKey,
					promise: verifyProjectFunding(
						projectId ?? undefined,
						providerOrderId,
					),
				};
			}

			const result = await inFlightRef.current.promise;
			if (cancelled) return;

			const resolvedProjectId = projectId ?? result.projectId;
			if (!resolvedProjectId) {
				setMessage(t("project.payments.verificationFailed"));
				toast.error(t("project.payments.verificationFailed"));
				return;
			}

			if (result.success && result.verified) {
				// A verified order reports its escrow rows. A top-up's one row is
				// `metadata.type: "top_up"`; it funded no milestone, so it gets the
				// copy `addFunds` in `use-project-funding.ts` gives the same outcome.
				const rows = result.transactions ?? [];
				const isTopUp = rows.length > 0 && rows.every(isEscrowTopUp);
				toast.success(
					t(
						isTopUp
							? "project.payments.fundsAdded"
							: "project.payments.statusFunded",
					),
				);
				navigate(`/projects/${resolvedProjectId}`, { replace: true });
				return;
			}

			if (result.success) {
				setMessage(t("project.payments.verificationPending"));
				toast.error(t("project.payments.verificationPending"));
				navigate(`/projects/${resolvedProjectId}`, { replace: true });
				return;
			}

			setMessage(t("project.payments.verificationFailed"));
			toast.error(result.error || t("project.payments.verificationFailed"));
			navigate(`/projects/${resolvedProjectId}`, { replace: true });
		}

		void run();

		return () => {
			cancelled = true;
		};
	}, [navigate, projectIdParam, searchParams, t]);

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted px-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>{t("project.payments.title")}</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-sm text-muted-foreground">{message}</p>
				</CardContent>
			</Card>
		</div>
	);
}
